import React, { useState, useEffect } from 'react';

const CustomDeviceModal = ({ userId, isDarkMode, onClose, editMode = false, deviceData = null }) => {
  const [deviceName, setDeviceName] = useState('');
  const [deviceSerial, setDeviceSerial] = useState('');
  const [selectedBoard, setSelectedBoard] = useState('');
  const [boardsCatalog, setBoardsCatalog] = useState([]);
  const [dashboardControlsCatalog, setDashboardControlsCatalog] = useState([]);
  const [controlsCatalog, setControlsCatalog] = useState([]);
  const [serialError, setSerialError] = useState('');

  const [controlsAdded, setControlsAdded] = useState([]);
  const [tempControl, setTempControl] = useState({
    type: '',
    name: '',
    description: '',
    unit: '',
    pin: ''
  });

  const [loading, setLoading] = useState(true);

  const API_BASE_URL = 'https://holmio.net/app/api.php';
  const bgColorClass = isDarkMode ? 'bg-gray-800' : 'bg-white';
  const inputBgClass = isDarkMode
    ? 'bg-gray-700 text-white border-gray-600'
    : 'bg-gray-50 text-gray-900 border-gray-300';
  const buttonClass = isDarkMode
    ? 'bg-green-600 hover:bg-green-700 text-white'
    : 'bg-green-500 hover:bg-green-600 text-white';
  const cancelButtonClass = isDarkMode
    ? 'bg-gray-600 hover:bg-gray-700 text-white'
    : 'bg-gray-400 hover:bg-gray-500 text-white';

  useEffect(() => {
    loadCatalogs();
  }, []);

  useEffect(() => {
    if (editMode && deviceData) {
      setDeviceName(deviceData.name || '');
      setDeviceSerial(deviceData.serial || '');
      setSelectedBoard(deviceData.board || '');
      // Mapear controles del backend al formato esperado por el modal
      const mappedControls = (deviceData.controls || []).map(c => ({
        type: c.type || 'sensor',
        name: c.control_name,
        description: c.description || '',
        unit: c.unit || '',
        pin: c.pin_number || ''
      }));
      setControlsAdded(mappedControls);
    }
  }, [editMode, deviceData]);
  const validateSerial = async (serial) => {
  if (!serial) {
    setSerialError('');
    return;
  }
  try {
    const res = await fetch(`${API_BASE_URL}/check_serial?serial=${serial}`);
    const data = await res.json();
    if (data.exists) {
      setSerialError('Este número de serie ya está registrado.');
    } else {
      setSerialError('');
    }
  } catch (err) {
    console.error('Error validando serial:', err);
    setSerialError('Error al validar el serial.');
  }
};


  const loadCatalogs = async () => {
    try {
      const [boardsRes, dashRes, controlsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/boards_catalog`),
        fetch(`${API_BASE_URL}/dashboard_controls`),
        fetch(`${API_BASE_URL}/controls_catalog`)
      ]);

      const boardsData = await boardsRes.json();
      const dashData = await dashRes.json();
      const controlsData = await controlsRes.json();

      if (boardsData.status === 'success') setBoardsCatalog(boardsData.data);
      if (dashData.status === 'success') setDashboardControlsCatalog(dashData.data);
      if (controlsData.status === 'success') setControlsCatalog(controlsData.data);

      setLoading(false);
    } catch (error) {
      console.error('Error cargando catálogos:', error);
      setLoading(false);
    }
  };

  const handleAddControl = () => {
    if (!tempControl.type || !tempControl.name) {
      alert('Selecciona tipo y nombre del control');
      return;
    }

    setControlsAdded([...controlsAdded, { ...tempControl }]);
    setTempControl({ type: '', name: '', description: '', unit: '', pin: '' });
  };

  const handleRemoveControl = (index) => {
    setControlsAdded(controlsAdded.filter((_, i) => i !== index));
  };

  const handleSaveDevice = async () => {
    if (!deviceName || !deviceSerial || !selectedBoard) {
      alert('Completa el nombre, serie y selecciona un board.');
      return;
    }
    if (serialError) {
      alert('Corrige el error en el número de serie antes de continuar.');
      return;
    }

    const payload = {
      user_id: userId,
      name: deviceName,
      serial: deviceSerial,
      board: selectedBoard,
      controls: controlsAdded
    };

    let url = `${API_BASE_URL}/custom_devices`;
    let method = 'POST';

    if (editMode && deviceData?.id) {
      payload.id = deviceData.id;
      method = 'PUT';
    }

    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.status === 'success') {
        alert(editMode ? 'Dispositivo actualizado correctamente.' : 'Dispositivo guardado correctamente.');
        onClose(); // Cerramos el modal y recargamos lista en el parent
      } else {
        alert(`Error: ${data.message}`);
      }
    } catch (err) {
      alert(`Error al guardar dispositivo: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl">
          <p className="text-lg font-semibold">Cargando catálogos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`p-6 rounded-lg shadow-xl max-w-3xl w-full ${bgColorClass} max-h-[90vh] overflow-y-auto`}>
        <h3 className="text-2xl font-bold mb-4 text-center">
          {editMode ? 'Editar Dispositivo Personalizado' : 'Agregar Dispositivo Personalizado'}
        </h3>

        {/* Datos del Dispositivo */}
        <div className="space-y-4 mb-6">
          <input
            type="text"
            placeholder="Nombre del Dispositivo"
            value={deviceName}
            onChange={(e) => setDeviceName(e.target.value)}
            className={`w-full px-4 py-2 rounded-lg ${inputBgClass}`}
          />
          <input
            type="text"
            placeholder="Serie del Dispositivo"
            value={deviceSerial}
            onChange={(e) => {
              setDeviceSerial(e.target.value);
              
            }}
            className={`w-full px-4 py-2 rounded-lg ${inputBgClass}`}
          />
          {serialError && <p className="text-red-500 text-sm mt-1">{serialError}</p>}
          <select
            value={selectedBoard}
            onChange={(e) => setSelectedBoard(e.target.value)}
            className={`w-full px-4 py-2 rounded-lg ${inputBgClass}`}
          >
            <option value="">Selecciona Board</option>
            {boardsCatalog.map((board) => (
              <option key={board.id} value={board.name}>
                {board.name}
              </option>
            ))}
          </select>
        </div>

        {/* Sección Agregar Control */}
        <div className="mb-6">
          <h4 className="text-lg font-bold mb-2">Agregar Control</h4>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-4">
            <select
              value={tempControl.type}
              onChange={(e) => setTempControl({ ...tempControl, type: e.target.value })}
              className={`px-3 py-2 rounded-lg ${inputBgClass}`}
            >
              <option value="">Tipo</option>
              <option value="dashboard">Dashboard Control</option>
              <option value="sensor">Sensor/Actuador</option>
            </select>

            <select
              value={tempControl.name}
              onChange={(e) => setTempControl({ ...tempControl, name: e.target.value })}
              className={`px-3 py-2 rounded-lg ${inputBgClass}`}
              disabled={!tempControl.type}
            >
              <option value="">Selecciona</option>
              {tempControl.type === 'dashboard' &&
                dashboardControlsCatalog.map((c) => (
                  <option key={c.id} value={c.type}>
                    {c.type}
                  </option>
                ))}
              {tempControl.type === 'sensor' &&
                controlsCatalog.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
            </select>

            <input
              type="text"
              placeholder="Descripción"
              value={tempControl.description}
              onChange={(e) => setTempControl({ ...tempControl, description: e.target.value })}
              className={`px-3 py-2 rounded-lg ${inputBgClass}`}
            />
            <input
              type="text"
              placeholder="Unidad"
              value={tempControl.unit}
              onChange={(e) => setTempControl({ ...tempControl, unit: e.target.value })}
              className={`px-3 py-2 rounded-lg ${inputBgClass}`}
            />
            <input
              type="number"
              placeholder="Pin"
              value={tempControl.pin}
              onChange={(e) => setTempControl({ ...tempControl, pin: e.target.value })}
              className={`px-3 py-2 rounded-lg ${inputBgClass}`}
            />
          </div>

          <button onClick={handleAddControl} className={`px-4 py-2 rounded-lg mb-4 ${buttonClass}`}>
            Agregar Control
          </button>

          {controlsAdded.length > 0 && (
            <table className="w-full text-sm border mt-2">
              <thead>
                <tr className="bg-gray-200 dark:bg-gray-700">
                  <th className="p-2">Tipo</th>
                  <th className="p-2">Nombre</th>
                  <th className="p-2">Descripción</th>
                  <th className="p-2">Unidad</th>
                  <th className="p-2">Pin</th>
                  <th className="p-2 text-center">Acción</th>
                </tr>
              </thead>
              <tbody>
                {controlsAdded.map((ctrl, idx) => (
                  <tr key={idx} className="border-t">
                    <td className="p-2">{ctrl.type}</td>
                    <td className="p-2">{ctrl.name}</td>
                    <td className="p-2">{ctrl.description}</td>
                    <td className="p-2">{ctrl.unit}</td>
                    <td className="p-2">{ctrl.pin}</td>
                    <td className="p-2 text-center">
                      <button
                        onClick={() => handleRemoveControl(idx)}
                        className="text-red-500 hover:text-red-700 font-bold"
                      >
                        X
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Botones finales */}
        <div className="flex justify-end space-x-2">
          <button onClick={onClose} className={`px-4 py-2 rounded-lg ${cancelButtonClass}`}>
            Cancelar
          </button>
          <button onClick={handleSaveDevice} className={`px-4 py-2 rounded-lg ${buttonClass}`}>
            {editMode ? 'Actualizar Dispositivo' : 'Guardar Dispositivo'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomDeviceModal;
